import axiosInstance from '../utils/axiosConfig';

/**
 * Personal Files Service - Manages personal user file operations with DocuWare
 * Following the same pattern as docuwareService.js
 */

/**
 * List all folders for the current user with file counts
 * @returns {Promise} - List of folders with {folder_name, file_count}
 */
export const listFolders = async () => {
  try {
    const response = await axiosInstance.get('/api/docuware/personal-files/folders/');
    return response.data;
  } catch (error) {
    console.error('Error listing folders:', error);
    throw error;
  }
};

/**
 * List all personal files for the current user
 * @param {string} folderName - Optional folder filter ('root' for root directory)
 * @returns {Promise} - List of files
 */
export const listFiles = async (folderName = null) => {
  try {
    const params = {};
    if (folderName) {
      params.folder_name = folderName;
    }
    
    const response = await axiosInstance.get('/api/docuware/personal-files/documents/', { params });
    return response.data;
  } catch (error) {
    console.error('Error listing personal files:', error);
    throw error;
  }
};

/**
 * Create a personal file record (step 1 of 2-step upload)
 * @param {string} fileName - The file name
 * @param {number} fileSize - File size in bytes
 * @param {string} folderName - Optional folder name
 * @returns {Promise} - Record creation response
 */
export const createFileRecord = async (fileName, fileSize, folderName = null) => {
  try {
    const data = {
      file_name: fileName,
      file_size: fileSize
    };
    
    if (folderName) {
      data.folder_name = folderName;
    }
    
    const response = await axiosInstance.post('/api/docuware/personal-files/create-record/', data);
    return response.data;
  } catch (error) {
    console.error('Error creating file record:', error);
    throw error;
  }
};

/**
 * Upload file content to an existing record (step 2 of 2-step upload)
 * @param {string} documentId - The document ID from createFileRecord
 * @param {File} file - The file to upload
 * @returns {Promise} - Upload response
 */
export const uploadFile = async (documentId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axiosInstance.post(
      `/api/docuware/personal-files/upload-file/${documentId}/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

/**
 * Complete file upload process (two-step: create record + upload file)
 * @param {File} file - The file to upload
 * @param {string} folderName - Optional folder name
 * @param {function} onProgress - Optional progress callback
 * @returns {Promise} - Complete upload response
 */
export const uploadFileComplete = async (file, folderName = null, onProgress = null) => {
  try {
    // Step 1: Create document record
    const createResponse = await createFileRecord(file.name, file.size, folderName);
    
    const documentId = createResponse.document_id;
    
    if (onProgress) onProgress(50);
    
    // Step 2: Upload file to the created record
    const uploadResponse = await uploadFile(documentId, file);
    
    if (onProgress) onProgress(100);
    
    return {
      documentId,
      fileName: file.name,
      ...uploadResponse
    };
  } catch (error) {
    console.error('Error in complete file upload:', error);
    throw error;
  }
};

/**
 * Download a personal file
 * @param {string} documentId - The document ID
 * @param {string} fileName - The filename for download
 * @returns {Promise} - Blob response
 */
export const downloadFile = async (documentId, fileName) => {
  try {
    const response = await axiosInstance.get(
      `/api/docuware/personal-files/download/${documentId}/`,
      {
        responseType: 'blob'
      }
    );
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return response.data;
  } catch (error) {
    console.error('Error downloading file:', error);
    throw error;
  }
};

/**
 * Delete a personal file
 * @param {string} documentId - The document ID
 * @returns {Promise} - Delete response
 */
export const deleteFile = async (documentId) => {
  try {
    const response = await axiosInstance.delete(
      `/api/docuware/personal-files/delete/${documentId}/`
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

/**
 * Rename a personal file
 * @param {string} documentId - The document ID
 * @param {string} newName - The new file name
 * @returns {Promise} - Rename response
 */
export const renameFile = async (documentId, newName) => {
  try {
    const response = await axiosInstance.patch(
      `/api/docuware/personal-files/rename/${documentId}/`,
      { new_name: newName }
    );
    return response.data;
  } catch (error) {
    console.error('Error renaming file:', error);
    throw error;
  }
};

/**
 * Move a file to a different folder
 * @param {string} documentId - The document ID
 * @param {string} targetFolder - The target folder name (null or 'root' for root)
 * @returns {Promise} - Move response
 */
export const moveFile = async (documentId, targetFolder) => {
  try {
    const response = await axiosInstance.patch(
      `/api/docuware/personal-files/move/${documentId}/`,
      { target_folder: targetFolder || 'root' }
    );
    return response.data;
  } catch (error) {
    console.error('Error moving file:', error);
    throw error;
  }
};

/**
 * Create a new folder
 * @param {string} folderName - The folder name
 * @param {string} folderColor - The folder color (hex format, default: #ffc20c)
 * @param {string} folderIcon - The folder icon (FontAwesome icon name, default: faFolder)
 * @returns {Promise} - Create folder response
 */
export const createFolder = async (folderName, folderColor = '#ffc20c', folderIcon = 'faFolder') => {
  try {
    const response = await axiosInstance.post(
      '/api/docuware/personal-files/folders/create/',
      { 
        folder_name: folderName,
        folder_color: folderColor,
        folder_icon: folderIcon
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error creating folder:', error);
    throw error;
  }
};

/**
 * Delete a folder (must be empty)
 * @param {string} folderName - The folder name
 * @returns {Promise} - Delete folder response
 */
export const deleteFolder = async (folderName) => {
  try {
    const response = await axiosInstance.delete(
      '/api/docuware/personal-files/folders/delete/',
      { data: { folder_name: folderName } }
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting folder:', error);
    throw error;
  }
};

/**
 * Get file preview data (for images and PDFs)
 * @param {string} documentId - The document ID
 * @returns {Promise} - Blob data for preview
 */
export const getFilePreview = async (documentId) => {
  try {
    const response = await axiosInstance.get(
      `/api/docuware/personal-files/download/${documentId}/`,
      {
        responseType: 'blob'
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error getting file preview:', error);
    throw error;
  }
};

export default {
  listFolders,
  listFiles,
  createFileRecord,
  uploadFile,
  uploadFileComplete,
  downloadFile,
  deleteFile,
  renameFile,
  moveFile,
  createFolder,
  deleteFolder,
  getFilePreview
};

